'use client';

/**
 * IntakeWizard Component
 *
 * A multi-step intake wizard for loan applications.
 * Template-driven with auth checkpoint support and localStorage persistence.
 *
 * @see Issue #432 - Templated Loan Intake Wizard
 */

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { IntakeCondition, IntakeFlowTemplate } from '@ops-matrix/shared-types';
import { AnimatePresence, motion } from 'framer-motion';

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import type { NeonAccent } from '../../theme/palette';
import type { TemplatePreviewData } from '../ContractSelectionCard/ContractSelectionCard';
import { StepIndicator, type StepIndicatorStep } from '../FormWizard';
import { GlassButton } from '../GlassButton';
import { GlassCard } from '../GlassCard';
import { AuthCheckpoint } from './components/AuthCheckpoint';
import type { ContractSelectionExtras } from './components/IntakeStepRenderer';
import { IntakeStepRenderer } from './components/IntakeStepRenderer';
import { RATE_CALCULATOR_FIELD_NAMES, RateCalculatorPanel } from './components/RateCalculatorPanel';
import { getI18nString } from './intake-utils';

// ============================================================================
// Types
// ============================================================================

export interface IntakeWizardProps {
  /**
   * Intake flow template
   */
  template: IntakeFlowTemplate;

  /**
   * Initial form values (for resuming)
   */
  initialValues?: Record<string, unknown>;

  /**
   * Initial step index (for resuming)
   */
  initialStep?: number;

  /**
   * Whether user is authenticated
   */
  isAuthenticated?: boolean;

  /**
   * Callback when auth is required
   */
  onAuthRequired?: (currentStep: number, formData: Record<string, unknown>) => void;

  /**
   * Callback when wizard is completed
   */
  onComplete: (values: Record<string, unknown>) => void | Promise<void>;

  /**
   * Callback when values change
   */
  onChange?: (values: Record<string, unknown>) => void;

  /**
   * Callback when step changes
   */
  onStepChange?: (step: number, direction: 'next' | 'prev') => void;

  /**
   * Callback when wizard is cancelled
   */
  onCancel?: () => void;

  /**
   * Whether wizard is disabled
   */
  disabled?: boolean;

  /**
   * Whether wizard is submitting
   */
  isSubmitting?: boolean;

  /**
   * Neon accent color
   */
  neonAccent?: NeonAccent;

  /**
   * Labels for buttons
   */
  labels?: {
    next?: string;
    back?: string;
    submit?: string;
    cancel?: string;
  };

  /**
   * Additional class name
   */
  className?: string;

  // Analytics tracking (P2 - Issue #471)
  /**
   * Callback for field-level analytics tracking
   * Called when field focus/blur events occur
   */
  onFieldAnalytic?: (analytic: FieldAnalyticEvent) => void;

  /**
   * Callback for step-level analytics tracking
   * Called when step changes with timing data
   */
  onStepAnalytic?: (analytic: StepAnalyticEvent) => void;

  /**
   * Enable analytics tracking (defaults to true if callbacks provided)
   */
  analyticsEnabled?: boolean;

  /**
   * Submission ID for associating analytics (set after initial draft save)
   */
  submissionId?: string;

  /**
   * Issue #1382 — Optional callback to fetch an inline renderable preview
   * for a contract-package template when the user opens the card preview
   * dialog. Passed through to `IntakeStepRenderer → ContractSelectionCard`.
   */
  onFetchTemplatePreview?: (templateId: string) => Promise<TemplatePreviewData>;
  /**
   * Issue #1540 / #1533 Phase 3 — Optional bundle that turns the
   * contract-type cards into a two-level picker. Passed through verbatim.
   */
  contractSelectionExtras?: ContractSelectionExtras;

  /**
   * Optional slot rendered immediately ABOVE the wizard's navigation /
   * submit row on the last step. Used by website lead-capture forms to
   * inject TCPA/A2P SMS opt-in checkboxes per US carrier (TCR) approval
   * rules — the consent disclosure must sit directly above the submit
   * affordance.
   */
  submitFooter?: React.ReactNode;
}

// Analytics event types (P2 - Issue #471)
export interface FieldAnalyticEvent {
  submissionId?: string;
  stepIndex: number;
  fieldName: string;
  fieldType: string;
  focusedAt?: string;
  completedAt?: string;
  timeSpentMs?: number;
  changeCount?: number;
  errorCount?: number;
  wasAbandoned?: boolean;
}

export interface StepAnalyticEvent {
  submissionId?: string;
  stepIndex: number;
  stepName: string;
  enteredAt: string;
  exitedAt?: string;
  timeSpentMs?: number;
  direction?: 'next' | 'prev' | 'exit';
  completedFields: number;
  totalFields: number;
}

export interface IntakeWizardRef {
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  getFormData: () => Record<string, unknown>;
  getCurrentStep: () => number;
}

// ============================================================================
// Styled Components
// ============================================================================

const WizardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(3),
  maxWidth: 1100,
  margin: '0 auto',
  // Switch to vertical stack on tablets and smaller (< 900px)
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxWidth: 700,
  },
}));

const StepIndicatorSidebar = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: 240,
  paddingTop: theme.spacing(1),
  // Full width on tablets and smaller
  [theme.breakpoints.down('md')]: {
    width: '100%',
    paddingTop: 0,
  },
}));

const MainContent = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const StepContent = styled(motion.div)({
  width: '100%',
});

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
}));

const CancelButton = styled(Box)({
  marginRight: 'auto',
});

const StepTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  fontWeight: 600,
  marginBottom: theme.spacing(0.75),
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.95)' : theme.palette.text.primary,
}));

const StepDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  lineHeight: 1.5,
}));

// ============================================================================
// Animation Variants
// ============================================================================

const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 50 : -50,
    opacity: 0,
  }),
};

// ============================================================================
// Component
// ============================================================================

export const IntakeWizard = forwardRef<IntakeWizardRef, IntakeWizardProps>(
  (
    {
      template,
      initialValues = {},
      initialStep = 0,
      isAuthenticated = false,
      onAuthRequired,
      onComplete,
      onChange,
      onStepChange,
      onCancel,
      disabled = false,
      isSubmitting = false,
      neonAccent = 'primary',
      labels = {},
      className,
      // Analytics props (P2 - Issue #471)
      onFieldAnalytic,
      onStepAnalytic,
      analyticsEnabled,
      submissionId,
      // Issue #1382 — inline template preview
      onFetchTemplatePreview,
      // Issue #1540 — two-level contract+template picker
      contractSelectionExtras,
      submitFooter,
    },
    ref,
  ) => {
    // State
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [formData, setFormData] = useState<Record<string, unknown>>(initialValues);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [direction, setDirection] = useState(0);
    const [showAuthCheckpoint, setShowAuthCheckpoint] = useState(false);
    const [complianceError, setComplianceError] = useState<string | null>(null);

    // Analytics tracking state (P2 - Issue #471)
    const isAnalyticsEnabled = analyticsEnabled ?? Boolean(onFieldAnalytic || onStepAnalytic);
    const stepEnteredAtRef = useRef<string>(new Date().toISOString());
    const fieldFocusTimeRef = useRef<Record<string, { focusedAt: string; changeCount: number }>>({});
    const fieldErrorCountRef = useRef<Record<string, number>>({});

    // Track step timing for analytics
    useEffect(() => {
      if (isAnalyticsEnabled) {
        stepEnteredAtRef.current = new Date().toISOString();
      }
    }, [currentStep, isAnalyticsEnabled]);

    // Track template ID to detect template changes
    const prevTemplateIdRef = useRef(template.id);

    // Reset wizard state when template changes (but not on initial mount)
    useEffect(() => {
      if (prevTemplateIdRef.current !== template.id) {
        // Template has changed - reset to first step, clear errors, preserve formData
        // but merge with new initialValues (keeps user data like name/email)
        setCurrentStep(0);
        setErrors({});
        setDirection(0);
        setShowAuthCheckpoint(false);
        setComplianceError(null);
        // Merge existing formData with new initialValues to preserve shared fields
        setFormData((prev) => ({ ...prev, ...initialValues }));
        prevTemplateIdRef.current = template.id;
      }
    }, [template.id, initialValues]);

    // Evaluate conditional display for a step
    const evaluateStepCondition = useCallback(
      (condition: IntakeCondition | undefined): boolean => {
        if (!condition) return true; // No condition means always show
        const fieldValue = formData[condition.field];
        const conditionValue = condition.value;

        switch (condition.operator) {
          case 'equals':
            return String(fieldValue) === String(conditionValue);
          case 'not_equals':
            return String(fieldValue) !== String(conditionValue);
          case 'contains':
            return String(fieldValue || '').includes(String(conditionValue));
          case 'greater_than':
            return Number(fieldValue) > Number(conditionValue);
          case 'less_than':
            return Number(fieldValue) < Number(conditionValue);
          case 'in':
            return Array.isArray(conditionValue) && (conditionValue as string[]).includes(String(fieldValue));
          case 'not_in':
            return Array.isArray(conditionValue) && !(conditionValue as string[]).includes(String(fieldValue));
          case 'is_empty':
            return !fieldValue || String(fieldValue).trim() === '';
          case 'is_not_empty':
            return Boolean(fieldValue) && String(fieldValue).trim() !== '';
          default:
            return true;
        }
      },
      [formData],
    );

    // Get steps from template, filtering by conditional display
    const allSteps = useMemo(() => template.data.steps, [template]);
    const steps = useMemo(
      () => allSteps.filter((step) => evaluateStepCondition(step.conditionalDisplay)),
      [allSteps, evaluateStepCondition],
    );
    const currentStepData = steps[currentStep];
    const totalSteps = steps.length;
    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === totalSteps - 1;

    // Convert steps to StepIndicator format
    const indicatorSteps: StepIndicatorStep[] = useMemo(
      () =>
        steps.map((step) => ({
          id: step.id,
          title: getI18nString(step.name),
          description: getI18nString(step.description),
          optional: false,
        })),
      [steps],
    );

    // Analytics: Track field focus (P2 - Issue #471)
    const trackFieldFocus = useCallback(
      (fieldName: string) => {
        if (!isAnalyticsEnabled) return;
        fieldFocusTimeRef.current[fieldName] = {
          focusedAt: new Date().toISOString(),
          changeCount: fieldFocusTimeRef.current[fieldName]?.changeCount || 0,
        };
      },
      [isAnalyticsEnabled],
    );

    // Analytics: Track field blur and send analytics (P2 - Issue #471)
    const trackFieldBlur = useCallback(
      (fieldName: string, fieldType: string, hasValue: boolean) => {
        if (!isAnalyticsEnabled || !onFieldAnalytic) return;

        const focusInfo = fieldFocusTimeRef.current[fieldName];
        if (!focusInfo) return;

        const completedAt = new Date().toISOString();
        const focusedAt = new Date(focusInfo.focusedAt);
        const timeSpentMs = new Date(completedAt).getTime() - focusedAt.getTime();

        onFieldAnalytic({
          submissionId,
          stepIndex: currentStep,
          fieldName,
          fieldType,
          focusedAt: focusInfo.focusedAt,
          completedAt,
          timeSpentMs,
          changeCount: focusInfo.changeCount,
          errorCount: fieldErrorCountRef.current[fieldName] || 0,
          wasAbandoned: !hasValue,
        });

        // Reset focus tracking for this field
        delete fieldFocusTimeRef.current[fieldName];
      },
      [isAnalyticsEnabled, onFieldAnalytic, submissionId, currentStep],
    );

    // Analytics: Track step changes (P2 - Issue #471)
    const trackStepChange = useCallback(
      (direction: 'next' | 'prev' | 'exit') => {
        if (!isAnalyticsEnabled || !onStepAnalytic) return;

        const stepData = steps[currentStep];
        if (!stepData) return;

        const exitedAt = new Date().toISOString();
        const enteredAt = new Date(stepEnteredAtRef.current);
        const timeSpentMs = new Date(exitedAt).getTime() - enteredAt.getTime();

        // Count completed fields (fields with values)
        const completedFields = stepData.fields.filter((f) => {
          const value = formData[f.name];
          return value !== undefined && value !== null && value !== '';
        }).length;

        onStepAnalytic({
          submissionId,
          stepIndex: currentStep,
          stepName:
            typeof stepData.name === 'string' ? stepData.name : stepData.name?.default || `Step ${currentStep + 1}`,
          enteredAt: stepEnteredAtRef.current,
          exitedAt,
          timeSpentMs,
          direction,
          completedFields,
          totalFields: stepData.fields.length,
        });
      },
      [isAnalyticsEnabled, onStepAnalytic, submissionId, currentStep, steps, formData],
    );

    // Update form data
    const updateFormData = useCallback(
      (name: string, value: unknown) => {
        const newData = { ...formData, [name]: value };
        setFormData(newData);
        onChange?.(newData);

        // Track field change for analytics (P2 - Issue #471)
        if (isAnalyticsEnabled && fieldFocusTimeRef.current[name]) {
          fieldFocusTimeRef.current[name].changeCount += 1;
        }

        // Clear error when field is updated
        if (errors[name]) {
          setErrors((prev) => {
            const next = { ...prev };
            delete next[name];
            return next;
          });
        }
      },
      [formData, onChange, errors, isAnalyticsEnabled],
    );

    // Validate current step
    const validateStep = useCallback((): boolean => {
      const stepData = steps[currentStep];
      const newErrors: Record<string, string> = {};

      for (const field of stepData.fields) {
        if (field.required) {
          const value = formData[field.name];
          if (value === undefined || value === null || value === '') {
            newErrors[field.name] = `${field.label} is required`;
          }
        }

        // Additional validation based on field type
        if (field.validation && formData[field.name]) {
          const value = formData[field.name];

          if (field.validation.min !== undefined && typeof value === 'number') {
            if (value < field.validation.min) {
              newErrors[field.name] = field.validation.message || `Minimum value is ${field.validation.min}`;
            }
          }

          if (field.validation.max !== undefined && typeof value === 'number') {
            if (value > field.validation.max) {
              newErrors[field.name] = field.validation.message || `Maximum value is ${field.validation.max}`;
            }
          }

          if (field.validation.pattern && typeof value === 'string') {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(value)) {
              newErrors[field.name] = field.validation.message || 'Invalid format';
            }
          }
        }
      }

      // Check compliance questions on last step (before submission)
      if (isLastStep && template.data.complianceQuestions?.length > 0) {
        for (const question of template.data.complianceQuestions) {
          // Check linked form field first, then manual checkbox
          let satisfied = false;
          if (question.linkedField) {
            const fieldValue = formData[question.linkedField];
            satisfied = question.linkedFieldAcceptedValues
              ? question.linkedFieldAcceptedValues.includes(String(fieldValue))
              : Boolean(fieldValue);
          }
          if (!satisfied) {
            satisfied = formData[`compliance_${question.id}`] === true;
          }
          if (question.requiredAnswer && !satisfied) {
            setComplianceError(question.blockingMessage || 'You must acknowledge this requirement to proceed.');
            setErrors(newErrors);
            return false;
          }
        }
      }

      setComplianceError(null);
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }, [currentStep, steps, formData, isLastStep, template.data.complianceQuestions]);

    // Go to specific step
    const goToStep = useCallback(
      (step: number, trackDirection: 'next' | 'prev' = 'next') => {
        if (step >= 0 && step < totalSteps && !disabled) {
          // Track step analytics before changing step (P2 - Issue #471)
          trackStepChange(trackDirection);

          setDirection(step > currentStep ? 1 : -1);
          setCurrentStep(step);
          onStepChange?.(step, step > currentStep ? 'next' : 'prev');
        }
      },
      [totalSteps, disabled, currentStep, onStepChange, trackStepChange],
    );

    // Next step
    const nextStep = useCallback(() => {
      if (!validateStep()) return;

      const nextStepIndex = currentStep + 1;

      // Check if next step requires auth
      if (nextStepIndex < totalSteps) {
        const nextStepData = steps[nextStepIndex];
        if (nextStepData.requiresAuth && !isAuthenticated) {
          setShowAuthCheckpoint(true);
          onAuthRequired?.(nextStepIndex, formData);
          return;
        }
      }

      if (isLastStep) {
        // Track exit analytics before completing (P2 - Issue #471)
        trackStepChange('exit');
        onComplete(formData);
      } else {
        goToStep(nextStepIndex, 'next');
      }
    }, [
      currentStep,
      isLastStep,
      goToStep,
      onComplete,
      formData,
      validateStep,
      steps,
      totalSteps,
      isAuthenticated,
      onAuthRequired,
      trackStepChange,
    ]);

    // Previous step
    const prevStep = useCallback(() => {
      if (!isFirstStep) {
        goToStep(currentStep - 1, 'prev');
      }
    }, [isFirstStep, goToStep, currentStep]);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        goToStep,
        nextStep,
        prevStep,
        getFormData: () => formData,
        getCurrentStep: () => currentStep,
      }),
      [goToStep, nextStep, prevStep, formData, currentStep],
    );

    // Handle auth checkpoint continue (after user authenticates)
    const handleAuthContinue = useCallback(() => {
      setShowAuthCheckpoint(false);
      goToStep(currentStep + 1);
    }, [goToStep, currentStep]);

    // Button labels
    const buttonLabels = {
      next: labels.next || 'Continue',
      back: labels.back || 'Back',
      submit: labels.submit || 'Submit Application',
      cancel: labels.cancel || 'Cancel',
    };

    // Check if current step is the rate calculator step
    const isRateCalculatorStep = currentStepData?.id === 'step-rate-calculator';

    // Extra fields on rate calculator steps not handled by RateCalculatorPanel (e.g. DSCR-specific)
    const extraRateCalcFields = useMemo(() => {
      if (!isRateCalculatorStep || !currentStepData?.fields) return [];
      return currentStepData.fields.filter((f) => !RATE_CALCULATOR_FIELD_NAMES.has(f.name));
    }, [isRateCalculatorStep, currentStepData?.fields]);

    // Responsive breakpoint for layout switch
    // Use 'md' (900px) breakpoint - vertical sidebar on desktop, horizontal on tablets/mobile
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

    return (
      <WizardContainer className={className}>
        {/* Step Indicator - Sidebar on desktop, top on mobile */}
        <StepIndicatorSidebar>
          <StepIndicator
            steps={indicatorSteps}
            currentStep={currentStep}
            size={isCompact ? 'small' : 'medium'}
            orientation={isCompact ? 'horizontal' : 'vertical'}
            clickable={false}
            neonAccent={neonAccent}
          />
        </StepIndicatorSidebar>

        {/* Auth Checkpoint Modal */}
        {showAuthCheckpoint && (
          <AuthCheckpoint
            open={showAuthCheckpoint}
            onClose={() => setShowAuthCheckpoint(false)}
            onContinue={handleAuthContinue}
            stepName={getI18nString(steps[currentStep + 1]?.name) || 'Next Step'}
          />
        )}

        {/* Main Content Area */}
        <MainContent>
          <GlassCard>
            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <AnimatePresence mode="wait" custom={direction}>
                <StepContent
                  key={currentStep}
                  custom={direction}
                  variants={stepVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                >
                  <StepTitle>{getI18nString(currentStepData?.name)}</StepTitle>
                  <StepDescription>{getI18nString(currentStepData?.description)}</StepDescription>

                  {/* Rate Calculator or Regular Step */}
                  {isRateCalculatorStep && template.data.rateCalculatorConfig ? (
                    <>
                      <RateCalculatorPanel
                        config={template.data.rateCalculatorConfig}
                        formData={formData}
                        onChange={updateFormData}
                        errors={errors}
                        disabled={disabled}
                      />
                      {/* Render extra fields not handled by RateCalculatorPanel (e.g. DSCR-specific fields) */}
                      {extraRateCalcFields.length > 0 && (
                        <IntakeStepRenderer
                          step={{ ...currentStepData, fields: extraRateCalcFields }}
                          formData={formData}
                          onChange={updateFormData}
                          errors={errors}
                          disabled={disabled}
                          neonAccent={neonAccent}
                          onFieldFocus={isAnalyticsEnabled ? trackFieldFocus : undefined}
                          onFieldBlur={isAnalyticsEnabled ? trackFieldBlur : undefined}
                          onFetchTemplatePreview={onFetchTemplatePreview}
                          contractSelectionExtras={contractSelectionExtras}
                        />
                      )}
                    </>
                  ) : (
                    <IntakeStepRenderer
                      step={currentStepData}
                      formData={formData}
                      onChange={updateFormData}
                      errors={errors}
                      disabled={disabled}
                      neonAccent={neonAccent}
                      // Analytics callbacks (P2 - Issue #471)
                      onFieldFocus={isAnalyticsEnabled ? trackFieldFocus : undefined}
                      onFieldBlur={isAnalyticsEnabled ? trackFieldBlur : undefined}
                      onFetchTemplatePreview={onFetchTemplatePreview}
                      contractSelectionExtras={contractSelectionExtras}
                    />
                  )}
                </StepContent>
              </AnimatePresence>

              {/* Compliance Error Alert */}
              {complianceError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {complianceError}
                </Alert>
              )}

              {/* Submit footer slot — TCPA/A2P SMS opt-in lives here on the last step */}
              {isLastStep && submitFooter}

              {/* Navigation Buttons */}
              <ButtonContainer>
                {onCancel && (
                  <CancelButton>
                    <GlassButton variant="ghost" onClick={onCancel} disabled={disabled || isSubmitting} size="medium">
                      {buttonLabels.cancel}
                    </GlassButton>
                  </CancelButton>
                )}
                {!isFirstStep && (
                  <GlassButton variant="outlined" onClick={prevStep} disabled={disabled || isSubmitting} size="medium">
                    {buttonLabels.back}
                  </GlassButton>
                )}
                <GlassButton
                  variant="gradient"
                  onClick={nextStep}
                  disabled={disabled || isSubmitting}
                  loading={isSubmitting && isLastStep}
                  size="medium"
                >
                  {isLastStep ? buttonLabels.submit : buttonLabels.next}
                </GlassButton>
              </ButtonContainer>
            </Box>
          </GlassCard>
        </MainContent>
      </WizardContainer>
    );
  },
);

IntakeWizard.displayName = 'IntakeWizard';

export default IntakeWizard;
